import { useMemo } from 'react';
import type { ProspectLoan } from '../../types';
import { extractEncompassLoCompBps } from '@/utils/loCompDerived';

export interface LocrEligibility {
  /** When false, the LOCR input is disabled in the aside; UI shows `reason`. */
  allowEdit: boolean;
  /** Human-readable explanation. Null when allowEdit=true. */
  reason: string | null;
}

/**
 * Phase 2 QA Fix 5 — LOCR eligibility gate.
 *
 * LOCR is a reduction to the LO Compensation built into the lender price. If no
 * ProfitMargin adjustment with description "LO Compensation" exists on the loan's
 * rate_lock_buy_side_adjustments (e.g. LW26042298), there's nothing to reduce and
 * the input should be disabled.
 *
 * Reuses the exact rule from `extractEncompassLoCompBps` in
 * src/utils/loCompDerived.ts (case-insensitive, trimmed description match on
 * 'lo compensation'). Server mirrors this in validateLocrEligibility at
 * supabase/functions/enqueue-encompass-write/index.ts.
 */
export function useLocrEligibility(loan: ProspectLoan): LocrEligibility {
  const encompassLoCompBps = useMemo(
    () => extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments),
    [loan.rate_lock_buy_side_adjustments]
  );

  if (encompassLoCompBps === 0) {
    return {
      allowEdit: false,
      reason: 'No LO Compensation built into this price'
    };
  }

  return { allowEdit: true, reason: null };
}
